import { useCallback, useEffect, useState } from "react";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Switch } from "@/components/ui/switch";
import { AdminHeading } from "@/pages/admin/AdminShell";

const inp = "w-full border border-[#241914]/20 bg-white/60 px-3 py-2 text-sm outline-none focus:border-[#C6A76A]";

function CityRow({ c, onSaved, onDelete }) {
  const { t } = useLang();
  const [v, setV] = useState(c);
  const dirty = JSON.stringify(v) !== JSON.stringify(c);
  const save = async () => {
    try {
      const { data } = await api.put(`/admin/cities/${c.id}`, { name: v.name, fee: Number(v.fee), eta: v.eta || "", active: v.active });
      onSaved(data);
      toast.success(t("a_saved"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="grid grid-cols-2 items-center gap-3 border-b border-[#241914]/10 py-3 md:grid-cols-[1.4fr_0.8fr_1.2fr_auto_auto_auto]" data-testid="admin-city-fee-row">
      <input className={inp} value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid={`admin-city-name-${c.id}`} />
      <input className={inp} type="number" min="0" value={v.fee} onChange={(e) => setV({ ...v, fee: e.target.value })} dir="ltr" data-testid={`admin-city-fee-${c.id}`} />
      <input className={inp} value={v.eta || ""} placeholder={t("a_eta")} onChange={(e) => setV({ ...v, eta: e.target.value })} data-testid={`admin-city-eta-${c.id}`} />
      <Switch checked={v.active} onCheckedChange={(a) => setV({ ...v, active: a })} data-testid={`admin-city-active-${c.id}`} />
      <button onClick={save} disabled={!dirty} className="p-2 disabled:opacity-30" data-testid={`admin-city-save-${c.id}`} aria-label={t("a_save")}><Save className="h-4 w-4" strokeWidth={1.3} /></button>
      <button onClick={() => onDelete(c)} className="p-2 hover:text-[#9b2c2c]" data-testid={`admin-city-delete-${c.id}`} aria-label={t("a_delete")}><Trash2 className="h-4 w-4" strokeWidth={1.3} /></button>
    </div>
  );
}

export default function AdminCities() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [n, setN] = useState({ name: "", fee: "", eta: "" });

  const load = useCallback(() => {
    api.get("/admin/cities").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([]));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/cities", { name: n.name, fee: Number(n.fee), eta: n.eta, active: true });
      setN({ name: "", fee: "", eta: "" });
      load();
    } catch (ex) {
      toast.error(errMsg(ex));
    }
  };

  const del = async (c) => {
    if (!window.confirm(`${t("a_confirm_delete")}\n${c.name}`)) return;
    await api.delete(`/admin/cities/${c.id}`);
    load();
  };

  return (
    <div data-testid="admin-cities">
      <AdminHeading title={t("a_delivery")} />
      <form onSubmit={add} className="mb-10 grid grid-cols-1 gap-3 bg-[#0B0B0C] p-5 sm:grid-cols-[1.4fr_0.8fr_1.2fr_auto]">
        <input required className={`${inp} !bg-transparent text-[#F7F3EB] !border-[#F7F3EB]/25`} placeholder={t("a_city_name")} value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} data-testid="admin-new-city-name" />
        <input required type="number" min="0" step="0.01" className={`${inp} !bg-transparent text-[#F7F3EB] !border-[#F7F3EB]/25`} placeholder={t("a_fee")} value={n.fee} onChange={(e) => setN({ ...n, fee: e.target.value })} dir="ltr" data-testid="admin-new-city-fee" />
        <input className={`${inp} !bg-transparent text-[#F7F3EB] !border-[#F7F3EB]/25`} placeholder={t("a_eta")} value={n.eta} onChange={(e) => setN({ ...n, eta: e.target.value })} data-testid="admin-new-city-eta" />
        <button className="flex items-center justify-center gap-2 bg-[#C6A76A] px-5 py-2 text-sm text-[#0B0B0C]" data-testid="admin-add-city-btn"><Plus className="h-4 w-4" /> {t("a_add_city")}</button>
      </form>
      {list.length === 0 ? (
        <p className="py-10 text-center text-[#241914]/70" data-testid="admin-cities-empty">{t("a_no_cities")}</p>
      ) : (
        <div className="border-t border-[#241914]/10">
          <div className="hidden grid-cols-[1.4fr_0.8fr_1.2fr_auto_auto_auto] gap-3 py-2 text-xs text-[#241914]/60 md:grid">
            <span>{t("a_city_name")}</span><span>{t("a_fee")}</span><span>{t("a_eta")}</span><span>{t("a_active")}</span><span /><span />
          </div>
          {list.map((c) => (
            <CityRow key={c.id} c={c} onDelete={del} onSaved={(d) => setList((l) => l.map((x) => (x.id === d.id ? d : x)))} />
          ))}
        </div>
      )}
    </div>
  );
}
